import { getOperators, inputFieldType } from "components/QueryBuilder/utilities";
import { getAllocationProgramProjects, getFinopsResourceMappingList } from "service/service";

export const fetchFieldOptionsHelper = async (fieldKey: string, queryPreference: any, setKeyFields: any) => {
    try {
        const fieldsRes = await getFinopsResourceMappingList({ searchField: fieldKey, searchQuery: queryPreference });
        if (fieldsRes.success) {
            const options = fieldsRes.data.result.map((x: any) => {
                return x === null ? '' : x;
            });
            setKeyFields((prevFields: any) => ({
                ...prevFields,
                [fieldKey]: {
                    ...prevFields[fieldKey],
                    fieldOptions: {
                        ...(prevFields[fieldKey]?.fieldOptions || {}),
                        options
                    }
                }
            }));
            return options;
        }
    } catch (error) {
        console.error(error);
        return [];
    }
};

const fieldOptions = {
    autocomplete: [
        'Resource',
        'ResourceGroup',
        'Subscription',
        'MeterCategory',
        'BusinessSector',
        'ClusterName',
        'Sector',
        'Environment',
        'ConsumedService',
        'Program',
        'ProjectName',
        'PepWiseSector',
        'PepWiseEnvironment',
        'PepWiseProgram',
        'PepWiseProject'
    ],
    textfield: [''],
    boolean: ['']
};

export const fetchKeyFieldsData = async () => {
    try {
        const fieldsRes = await getFinopsResourceMappingList({ searchField: 'keyFields' });
        if (fieldsRes.success) {
            const keyFieldRes = fieldsRes.data.result;
            const fields = await Promise.all(
                keyFieldRes.map(async (field: string) => {
                    let fieldType = inputFieldType.autocomplete;
                    if (fieldOptions.autocomplete.includes(field)) {
                        fieldType = inputFieldType.autocomplete;
                    } else if (fieldOptions.textfield.includes(field)) {
                        fieldType = inputFieldType.textfield;
                    }
                    return {
                        [field]: {
                            name: field,
                            label: field.replace(/([a-z])([A-Z][a-z]+)$/, '$1 $2').trim(),
                            fieldOptions: {
                                fieldInput: fieldType,
                                options: null,
                                operators: fieldOptions.boolean?.includes(field) ? getOperators('boolean') : getOperators('string'),
                                valueType: fieldOptions.boolean?.includes(field) ? 'boolean' : 'string'
                            }
                        }
                    };
                })
            );
            return fields.reduce((acc, field) => ({ ...acc, ...field }), {});
        }
    } catch (error) {
        console.error('Error fetching fields:', error);
        return {};
    }
};

interface KeyFieldsResult {
    fieldsData: any;
    resourceTypeCost: any[];
}

export const fetchKeyFieldsListData = async (): Promise<KeyFieldsResult> => {
    try {
        const fieldsRes = await getFinopsResourceMappingList({ searchField: 'keyFields', includeCost: true });
        if (fieldsRes.success) {
            const keyFieldRes = fieldsRes.data.result;
            const resourceTypeCost = fieldsRes.data.resourceTypeCost ?? [];
             const fields = await Promise.all(
                keyFieldRes.map(async (field: string) => {
                    let fieldType = inputFieldType.autocomplete;
                    if (fieldOptions.autocomplete.includes(field)) {
                        fieldType = inputFieldType.autocomplete;
                    } else if (fieldOptions.textfield.includes(field)) {
                        fieldType = inputFieldType.textfield;
                    }
                    return {
                        [field]: {
                            name: field,
                            label: field.replace(/([a-z])([A-Z][a-z]+)$/, '$1 $2').trim(),
                            fieldOptions: {
                                fieldInput: fieldType,
                                options: null,
                                operators: fieldOptions.boolean?.includes(field) ? getOperators('boolean') : getOperators('string'),
                                valueType: fieldOptions.boolean?.includes(field) ? 'boolean' : 'string'
                            }
                        }
                    };
                })
            );
            const fieldsData = fields.reduce((acc, field) => ({ ...acc, ...field }), {});
            return { fieldsData, resourceTypeCost };
        }

        return { fieldsData: {}, resourceTypeCost: [] };
    } catch (error) {
        console.error('Error fetching fields:', error);
        return { fieldsData: {}, resourceTypeCost: [] };
    }
};

export const formatAllocPercent = (allocPercent: string) => {
	if (!allocPercent) return null;
	const percentages = allocPercent.split(',').map((val) => parseInt(val.trim(), 10));
	let groups: string[] = [];
	let currentGroup: number[] = [];
	let currentSum = 0;
	percentages.forEach((value) => {
		if (currentSum + value <= 100) {
			currentGroup.push(value);
			currentSum += value;
		} else {
			const groupValues = currentGroup.map((v) => `${v}%`).join(', ');
			groups.push('(' + groupValues + ')');
			currentGroup = [value];
			currentSum = value;
		}
	});
	if (currentGroup.length > 0) {
		const groupValues = currentGroup.map((v) => `${v}%`).join(', ');
		groups.push('(' + groupValues + ')');
	}
	return groups.join(' ');
};

interface Option {
	value: string;
	label: string;
}

export const getProgramProjectHelper = async (input: string, type: string, isAllocModal: boolean = false) => {
    try {
        const fieldsRes = await getAllocationProgramProjects({ searchText: input, searchType: type });
        const resData = Array.isArray(fieldsRes?.data?.result) ? fieldsRes.data.result : [];
        let options: Option[];
        if(type === 'program') {
            options = resData.map((program: any) => ({
                value: program.programId,
                label: program.programName
            }));
        }
        else {
            options = resData.map((project: any) => ({
                value: project.projectId,
                label: project.projectName
            }));
        }
        return options;
    } catch (error) {
        let errMsg = isAllocModal ? `${type}` : 'program/project';
        console.error(`Error fetching ${errMsg} options:`, error);
        return [];
    }
};

export const columnNames = [
    { name: 'Resource', label: 'Resource', isEditable: false },
    { name: 'Resourcetype', label: 'Resource Type', isEditable: false },
    { name: 'ResourceGroup', label: 'Resource Group', isEditable: false },
    { name: 'AllocPercent', label: 'Allocation %', isEditable: false },
    { name: 'PepwiseSector', label: 'Pepwise Sector', isEditable: true },
    { name: 'PepwiseEnvironment', label: 'Pepwise Environment', isEditable: true },
    { name: 'PepwiseProject', label: 'Pepwise Project', isEditable: true },
    { name: 'PepwiseProgram', label: 'Pepwise Program', isEditable: true },
    { name: 'BusinessSector', label: 'Business Sector', isEditable: false },
    { name: 'ProjectName', label: 'Project Name', isEditable: true },
    { name: 'Program', label: 'Program', isEditable: true },
    { name: 'Environment', label: 'Environment', isEditable: true },
    { name: 'Subscription', label: 'Subscription', isEditable: false },
    { name: 'ConsumedService', label: 'Consumed Service', isEditable: false },
    { name: 'location', label: 'Location', isEditable: false },
    { name: 'OrgGrpCodeProjectCode', label: 'Org Grp Code Project Code', isEditable: false },
    { name: 'projectno', label: 'Project No', isEditable: false },
    { name: 'BillingInformation', label: 'Billing Information', isEditable: false },
    { name: 'ClusterName', label: 'Cluster Name', isEditable: false },
    { name: 'ClusterJobSource', label: 'Cluster Job Source', isEditable: false },
    { name: 'DatabricksInstancePoolId', label: 'Databricks Instance Pool Id', isEditable: false },
    { name: 'DFSectorname', label: 'DF Sectorname', isEditable: false },
    { name: 'DFProjectcode', label: 'DF Projectcode', isEditable: false },
    { name: 'DFDataassetname', label: 'DF Data Assetname', isEditable: false },
    { name: 'SqlEndpointId', label: 'Sql Endpoint Id', isEditable: false },
    { name: 'kubernetespvcnamespace', label: 'Kubernetes pvc Namespace', isEditable: false },
];
export interface ResourceTypeCostItem {
	ResourceType: string;
	TotalCost: number;
	ResourceCount: number;
}

export interface ResourceTypeCostItem {
	ResourceType: string;
	TotalCost: number;
	ResourceCount: number;
}

export type CostDisplay =
	| { mode: 'single'; total: number; source: string }
	| { mode: 'dual'; azure: number; databricks: number; total: number }
	| { mode: 'multi'; items: { source: string; total: number }[]; total: number }
	| null;

const QUERY_PREF_RESOURCE_MAP: Record<
	string,
	{ types: string[]; source: string } | 'MULTI' | 'DUAL'
> = {
	Resource_flat: { types: ['Azure'], source: 'Azure' },
	SqlEndpointId_tree: { types: ['DBSQL Cluster'], source: 'DBSQL Cluster' },
	ClusterName_inttree: { types: ['Interactive Cluster'], source: 'Interactive Cluster' },
	ClusterJobSource_jobtree: { types: ['Job Cluster'], source: 'Job Cluster' },
	ResourceGroup_tree: 'MULTI',   // <-- shows every resource type individually
	Blank_flatall: 'DUAL'
};

// Only used for the dual-source (Blank Query) view — everything non-Azure rolls up as "Databricks" there
const DATABRICKS_RESOURCE_TYPES = ['Job Cluster', 'Databricks Instance Pool', 'Interactive Cluster', 'DBSQL Cluster'];

export function computeCostDisplay(queryPreference: string, resourceTypeCost: ResourceTypeCostItem[]): CostDisplay {
	if (!resourceTypeCost || resourceTypeCost.length === 0) return null;

	const sumByTypes = (types: string[]) =>
		resourceTypeCost
			.filter((item) => types.includes(item.ResourceType))
			.reduce((sum, item) => sum + Number(item.TotalCost || 0), 0);

	const sumAll = () => resourceTypeCost.reduce((sum, item) => sum + Number(item.TotalCost || 0), 0);

	const mapping = QUERY_PREF_RESOURCE_MAP[queryPreference];

	if (mapping === 'MULTI') {
		const items = resourceTypeCost
			.map((item) => ({ source: item.ResourceType, total: Number(item.TotalCost || 0) }))
			.sort((a, b) => b.total - a.total);
		return { mode: 'multi', items, total: sumAll() };
	}

	if (mapping === 'DUAL') {
		const azure = sumByTypes(['Azure']);
		const databricks = sumByTypes(DATABRICKS_RESOURCE_TYPES);
		return { mode: 'dual', azure, databricks, total: azure + databricks };
	}

	if (mapping) {
		return { mode: 'single', total: sumByTypes(mapping.types), source: mapping.source };
	}

	// Fallback — unknown/unmapped preference, show combined total
	return { mode: 'single', total: sumAll(), source: 'All Resources' };
}

export const formatCurrency = (value: number) =>
	`$${Number(value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;